/**
 * LocationService
 * Service for Location entity operations
 * Route: api/locations
 * Controller: LocationsController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * LocationService
 * Handles location CRUD operations and custom endpoints
 * Maps to /api/locations endpoint
 */
export class LocationService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LocationService.DEFAULT_SERVICE_PATH,
            basePath: LocationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get all active locations (IsActive = true)
     * GET /api/locations/active
     * @returns Array of active locations
     */
    async getAllActive() {
        const response = await this.getAllActiveRaw();
        return response.data ?? [];
    }
    /**
     * Get all active locations (raw)
     * @returns ResponseData with array of active locations
     */
    getAllActiveRaw() {
        const endpoint = this.getEndpoint('active', 'active');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get location with enriched model view by ID
     * GET /api/locations/view/{id}
     * @param id - Location ID
     * @returns Enriched location model view
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Location view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get location view by ID (raw)
     * @param id - Location ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all location model views (enriched with related data)
     * GET /api/locations/view
     * @returns Array of enriched location model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all location model views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all active location model views (IsActive = true, enriched with related data)
     * GET /api/locations/active/view
     * @returns Array of active enriched location model views
     */
    async getAllActiveViews() {
        const response = await this.getAllActiveViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all active location model views (raw)
     * @returns ResponseData with array of active enriched model views
     */
    getAllActiveViewsRaw() {
        const endpoint = this.getEndpoint('activeViews', 'active/view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
LocationService.DEFAULT_SERVICE_PATH = 'locations';
LocationService.BASE_PATH = 'api/locations';
//# sourceMappingURL=LocationService.js.map